import type { MapLibreRenderImage } from '../map/mapLibreRenderPlan';
import {
  SymbolImportError,
  SYMBOL_LIMITS,
  type SymbolAsset,
  type SymbolMimeType,
  type SymbolGraphic,
  symbolHash,
} from './model';
import {
  backingCanvasPath,
  backingGeometry,
  backingKey,
  type BackingGeometry,
  type SymbolBacking,
  type SymbolPresentation,
} from './backing';
import { builtinSymbol } from './catalogue';
import { graphicKey, monochromeSvg } from './tint';
import { measureSymbolInk, type SymbolInk } from './ink';

export interface PreparedSymbolImage extends MapLibreRenderImage {
  readonly preview: string;
  readonly ink?: SymbolInk;
  readonly composition?: {
    readonly backing: SymbolBacking;
    readonly geometry: BackingGeometry;
    readonly foreground: PreparedSymbolImage;
    readonly foregroundSize: number;
    readonly foregroundOpacity: number;
  };
}
export interface SymbolImageRequest extends SymbolPresentation {
  readonly graphic: SymbolGraphic;
  readonly tint?: string | undefined;
}
export function preparedSymbolCost(image: PreparedSymbolImage): number {
  return (
    image.data.byteLength +
    image.preview.length * 2 +
    (image.composition ? preparedSymbolCost(image.composition.foreground) : 0)
  );
}
export type PreparedSymbolCatalogue = ReadonlyMap<string, PreparedSymbolImage>;
export const EMPTY_SYMBOL_CATALOGUE: PreparedSymbolCatalogue = new Map();

/** Always decode an already-validated source. URL and native image ownership end here. */
export async function withSymbolImage<T>(
  bytes: Uint8Array,
  mime: SymbolMimeType,
  consume: (image: HTMLImageElement) => Promise<T> | T,
  signal?: AbortSignal,
): Promise<T> {
  signal?.throwIfAborted();
  const url = URL.createObjectURL(new Blob([new Uint8Array(bytes).buffer], { type: mime }));
  const image = new Image();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let abort: (() => void) | undefined;
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () =>
        reject(
          new SymbolImportError(
            'This symbol could not be decoded. Try exporting a transparent PNG.',
          ),
        );
      abort = () => reject(new DOMException('Symbol preparation cancelled.', 'AbortError'));
      signal?.addEventListener('abort', abort, { once: true });
      timer = setTimeout(
        () =>
          reject(
            new SymbolImportError('This symbol took too long to prepare. Try a simpler image.'),
          ),
        10_000,
      );
      image.src = url;
    });
    signal?.throwIfAborted();
    if (!image.naturalWidth || !image.naturalHeight)
      throw new SymbolImportError('This symbol has invalid image dimensions.');
    return await consume(image);
  } finally {
    clearTimeout(timer);
    if (abort) signal?.removeEventListener('abort', abort);
    image.onload = null;
    image.onerror = null;
    image.src = '';
    URL.revokeObjectURL(url);
  }
}
export function symbolCanvas(
  width: number,
  height: number,
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new SymbolImportError('This browser could not prepare the symbol image.');
  return { canvas, ctx };
}
export async function canvasPng(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new SymbolImportError('Could not prepare this PNG.')),
      'image/png',
    ),
  );
  return new Uint8Array(await blob.arrayBuffer());
}

/** Per-owner, byte-accounted LRU. Callers own returned catalogues and clear them on disposal. */
export class SymbolImageCache {
  #entries = new Map<string, PreparedSymbolImage>();
  #ink = new Map<string, SymbolInk>();
  #bytes = 0;
  get byteLength(): number {
    return this.#bytes;
  }
  get size(): number {
    return this.#entries.size;
  }
  clear(): void {
    this.#entries.clear();
    this.#ink.clear();
    this.#bytes = 0;
  }
  async prepare(
    asset: SymbolAsset,
    pixelRatio = 2,
    signal?: AbortSignal,
    logicalLongEdge = 80,
    tint?: string,
  ): Promise<PreparedSymbolImage> {
    signal?.throwIfAborted();
    const ratio = Math.max(1, Math.min(16, pixelRatio));
    const longEdge = Math.max(16, Math.min(2048, Math.ceil(logicalLongEdge * ratio)));
    const key = `placeio-symbol-${asset.sha256}-${tint?.toLowerCase() ?? 'original'}-${longEdge}-${ratio}`;
    const cached = this.#entries.get(key);
    if (cached) {
      this.#entries.delete(key);
      this.#entries.set(key, cached);
      return cached;
    }
    const prepared = await withSymbolImage(
      asset.bytes,
      asset.mimeType,
      (image) => {
        let ink = this.#ink.get(asset.sha256);
        if (!ink) {
          // Stable source-alpha metrics across UI, display and print densities. Never crop/recentre.
          const measureScale =
            asset.mimeType === 'image/svg+xml'
              ? 1024 / Math.max(asset.width, asset.height)
              : Math.min(1, 2048 / Math.max(asset.width, asset.height));
          const measured = symbolCanvas(
            Math.max(1, Math.round(asset.width * measureScale)),
            Math.max(1, Math.round(asset.height * measureScale)),
          );
          try {
            measured.ctx.drawImage(image, 0, 0, measured.canvas.width, measured.canvas.height);
            ink =
              measureSymbolInk(
                new Uint8Array(
                  measured.ctx.getImageData(0, 0, measured.canvas.width, measured.canvas.height)
                    .data,
                ),
                measured.canvas.width,
                measured.canvas.height,
              ) ?? undefined;
            if (!ink) throw new SymbolImportError('This symbol has no visible artwork.');
            if (this.#ink.size >= 128) this.#ink.delete(this.#ink.keys().next().value!);
            this.#ink.set(asset.sha256, ink);
          } finally {
            measured.canvas.width = measured.canvas.height = 0;
          }
        }
        const scale = longEdge / Math.max(asset.width, asset.height);
        const width = Math.max(1, Math.round(asset.width * scale)),
          height = Math.max(1, Math.round(asset.height * scale));
        const { canvas, ctx } = symbolCanvas(width, height);
        try {
          ctx.drawImage(image, 0, 0, width, height);
          if (tint) {
            ctx.globalCompositeOperation = 'source-in';
            ctx.fillStyle = tint;
            ctx.fillRect(0, 0, width, height);
            ctx.globalCompositeOperation = 'source-over';
          }
          const data = new Uint8Array(ctx.getImageData(0, 0, width, height).data);
          if (!data.some((value, index) => index % 4 === 3 && value > 0))
            throw new SymbolImportError('This symbol has no visible artwork.');
          return {
            id: key,
            width,
            height,
            data,
            pixelRatio: ratio,
            preview: canvas.toDataURL('image/png'),
            ink,
          };
        } finally {
          canvas.width = 0;
          canvas.height = 0;
        }
      },
      signal,
    );
    signal?.throwIfAborted();
    // Include the preview data URL's UTF-16 retention, not only decoded RGBA bytes.
    const cost = preparedSymbolCost;
    while (this.#bytes + cost(prepared) > SYMBOL_LIMITS.cacheBytes && this.#entries.size) {
      const first = this.#entries.entries().next().value!;
      this.#entries.delete(first[0]);
      this.#bytes -= cost(first[1]);
    }
    if (cost(prepared) <= SYMBOL_LIMITS.cacheBytes) {
      this.#entries.set(key, prepared);
      this.#bytes += cost(prepared);
    }
    return prepared;
  }
  async withBacking(
    foreground: PreparedSymbolImage,
    presentation: SymbolPresentation,
    signal?: AbortSignal,
  ): Promise<PreparedSymbolImage> {
    const backing = presentation.backing;
    if (!backing?.enabled) return foreground;
    signal?.throwIfAborted();
    const id = `${foreground.id}-backing-${await symbolHash(new TextEncoder().encode(backingKey(presentation)))}`;
    const cached = this.#entries.get(id);
    if (cached) {
      this.#entries.delete(id);
      this.#entries.set(id, cached);
      return cached;
    }
    const size = presentation.size ?? 80;
    const geometry = backingGeometry(backing, foreground, size);
    const rasterScale = Math.min(
      foreground.pixelRatio,
      2048 / Math.max(geometry.width, geometry.height),
    );
    const width = Math.max(1, Math.min(2048, Math.ceil(geometry.width * rasterScale))),
      height = Math.max(1, Math.min(2048, Math.ceil(geometry.height * rasterScale)));
    const source = symbolCanvas(foreground.width, foreground.height);
    const target = symbolCanvas(width, height);
    let image: PreparedSymbolImage;
    try {
      const pixels = source.ctx.createImageData(foreground.width, foreground.height);
      pixels.data.set(foreground.data);
      source.ctx.putImageData(pixels, 0, 0);
      const ctx = target.ctx;
      ctx.translate(width / 2, height / 2);
      ctx.scale(rasterScale, rasterScale);
      backingCanvasPath(ctx, backing, geometry);
      ctx.fillStyle = backing.fillColor;
      ctx.globalAlpha = backing.fillOpacity;
      ctx.fill();
      if (backing.borderWidth > 0 && backing.borderOpacity > 0) {
        ctx.strokeStyle = backing.borderColor;
        ctx.lineWidth = backing.borderWidth;
        ctx.lineJoin = 'round';
        ctx.globalAlpha = backing.borderOpacity;
        ctx.stroke();
      }
      ctx.globalAlpha = presentation.opacity ?? 1;
      const f = geometry.foreground;
      ctx.drawImage(source.canvas, f.x, f.y, f.width, f.height);
      image = {
        id,
        width,
        height,
        pixelRatio: foreground.pixelRatio,
        data: new Uint8Array(ctx.getImageData(0, 0, width, height).data),
        preview: target.canvas.toDataURL('image/png'),
        composition: {
          backing,
          geometry,
          foreground,
          foregroundSize: size,
          foregroundOpacity: presentation.opacity ?? 1,
        },
      };
    } finally {
      source.canvas.width = source.canvas.height = target.canvas.width = target.canvas.height = 0;
    }
    signal?.throwIfAborted();
    const cost = preparedSymbolCost(image);
    if (cost > SYMBOL_LIMITS.cacheBytes)
      throw new SymbolImportError(
        'This symbol background exceeds the image budget. Reduce its size or export resolution.',
      );
    while (this.#bytes + cost > SYMBOL_LIMITS.cacheBytes && this.#entries.size) {
      const first = this.#entries.entries().next().value!;
      this.#entries.delete(first[0]);
      this.#bytes -= preparedSymbolCost(first[1]);
    }
    this.#entries.set(id, image);
    this.#bytes += cost;
    return image;
  }
  async graphic(
    graphic: SymbolGraphic,
    assets: readonly SymbolAsset[],
    tint?: string,
    pixelRatio = 2,
    signal?: AbortSignal,
    logicalLongEdge = 80,
  ): Promise<PreparedSymbolImage> {
    if (typeof graphic === 'string') throw new SymbolImportError('An image symbol is required.');
    if (graphic.kind === 'builtin') {
      const builtin = builtinSymbol(graphic.id);
      return this.prepare(
        {
          id: graphic.id,
          name: builtin.name,
          mimeType: 'image/svg+xml',
          bytes: new TextEncoder().encode(builtin.svg),
          sha256: graphic.id,
          width: 15,
          height: 15,
        },
        pixelRatio,
        signal,
        logicalLongEdge,
        tint ?? '#000000',
      );
    }
    const asset = assets.find((a) => a.id === graphic.assetId);
    if (!asset) throw new SymbolImportError('A style references an unavailable symbol.');
    return this.prepare(
      asset,
      pixelRatio,
      signal,
      logicalLongEdge,
      tint && monochromeSvg(asset) ? tint : undefined,
    );
  }
  async graphics(
    requests: readonly SymbolImageRequest[],
    assets: readonly SymbolAsset[],
    pixelRatio = 2,
    signal?: AbortSignal,
    logicalLongEdge = 80,
  ): Promise<PreparedSymbolCatalogue> {
    const result = new Map<string, PreparedSymbolImage>();
    const retained = new Set<string>();
    let total = 0;
    for (const request of requests) {
      const key = graphicKey(request.graphic, request.tint, request);
      if (typeof request.graphic === 'string' || result.has(key)) continue;
      const original = await this.graphic(
        request.graphic,
        assets,
        request.tint,
        pixelRatio,
        signal,
        logicalLongEdge,
      );
      const image = await this.withBacking(original, request, signal);
      if (!retained.has(image.id)) total += preparedSymbolCost(image);
      retained.add(image.id);
      if (total > SYMBOL_LIMITS.cacheBytes)
        throw new SymbolImportError(
          'The prepared symbol variants exceed the image budget. Reduce the number of artwork colours or export resolution.',
        );
      result.set(key, image);
    }
    return result.size ? result : EMPTY_SYMBOL_CATALOGUE;
  }
  async catalogue(
    assets: readonly SymbolAsset[],
    pixelRatio = 2,
    signal?: AbortSignal,
  ): Promise<PreparedSymbolCatalogue> {
    const result = new Map<string, PreparedSymbolImage>();
    let total = 0;
    for (const asset of assets) {
      signal?.throwIfAborted();
      const image = await this.prepare(asset, pixelRatio, signal);
      total += image.data.byteLength + image.preview.length * 2;
      if (total > SYMBOL_LIMITS.cacheBytes)
        throw new SymbolImportError(
          'The prepared symbol library exceeds the image budget. Remove unused symbols or use a lower pixel ratio.',
        );
      result.set(asset.id, image);
    }
    return result;
  }
}
